/**
 * IFRAP-AI.fr - Admin Layout (Back-office)
 * Cockpit Opérationnel - Inspiration Apollo 11
 * 
 * Palette: Fond #050505, Ambre #FFB000, Vert #33FF00, Cyan #00FFFF
 * Typography: Space Grotesk (headings) + JetBrains Mono (data/logs)
 * 
 * RÈGLE: Lisibilité et clarté opérationnelle > Esthétique
 */

import React, { useState } from 'react';
import { Link, Outlet, useLocation, Navigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import CyberAgnesChat from '../components/CyberAgnesChat';

const AdminLayout = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Redirect to login if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  const navItems = [
    { 
      path: '/admin', 
      label: 'Dashboard', 
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
        </svg>
      )
    },
    { 
      path: '/admin/console', 
      label: 'Console Verbose', 
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
      ),
      highlight: true 
    },
    { 
      path: '/admin/documents', 
      label: 'Base de connaissances', 
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
    },
    { 
      path: '/admin/kpis', 
      label: 'KPIs', 
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
        </svg>
      ),
    },
    { 
      path: '/admin/osint', 
      label: 'OSINT', 
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      ),
    },
    { 
      path: '/admin/alerts', 
      label: 'Alertes', 
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
      ),
    },
    { 
      path: '/admin/distribution', 
      label: 'Listes diffusion', 
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
    },
    { 
      path: '/admin/summary', 
      label: 'Résumé IA', 
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
      ),
    },
    { 
      path: '/admin/costs', 
      label: 'Coûts LLM', 
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
    },
    { 
      path: '/admin/chronique', 
      label: 'Chronique Média', 
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11a7 7 0 01-7 7m0 0a7 7 0 01-7-7m7 7v4m0 0H8m4 0h4m-4-8a3 3 0 01-3-3V5a3 3 0 116 0v6a3 3 0 01-3 3z" />
        </svg>
      ),
      highlight: true,
    },
    { 
      path: '/admin/reports', 
      label: 'Rapports IFRAP', 
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      highlight: true,
    },
    { 
      path: '/admin/data-request', 
      label: 'Demande données', 
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
        </svg>
      ),
      highlight: true,
    },
    { 
      path: '/admin/scraper', 
      label: 'Scraper IFRAP', 
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
        </svg>
      ),
      highlight: true,
    },
    { 
      path: '/admin/universal-scraper', 
      label: 'Scraper Universel', 
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
        </svg>
      ),
      highlight: true,
    },
    { 
      path: '/admin/history', 
      label: 'Historique', 
      icon: (
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      ),
      highlight: true,
    },
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white flex">
      {/* Sidebar */}
      <aside 
        data-testid="sidebar"
        className={`${
          sidebarOpen ? 'w-64' : 'w-16'
        } bg-[#0a0a0a] border-r border-[#1a1a1a] transition-all duration-300 flex flex-col`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-4 border-b border-[#1a1a1a]">
          {sidebarOpen ? (
            <div className="flex items-center space-x-3">
              <div className="w-8 h-8 bg-[#FFB000] rounded flex items-center justify-center">
                <span className="text-black font-bold text-sm">IF</span>
              </div>
              <span className="text-[#FFB000] font-mono text-sm tracking-wider">IFRAP-AI</span>
            </div>
          ) : (
            <div className="w-8 h-8 bg-[#FFB000] rounded flex items-center justify-center mx-auto">
              <span className="text-black font-bold text-sm">IF</span>
            </div>
          )}
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4">
          {navItems.map(({ path, label, icon, highlight, disabled, phase }) => {
            const isActive = location.pathname === path;
            return (
              <Link
                key={path}
                to={disabled ? '#' : path}
                className={`flex items-center px-4 py-3 mx-2 rounded transition-all ${
                  disabled 
                    ? 'opacity-40 cursor-not-allowed' 
                    : isActive 
                      ? 'bg-[#1a1a1a] border-l-2 border-[#FFB000]' 
                      : 'hover:bg-[#1a1a1a]/50'
                } ${highlight && !disabled ? 'text-[#33FF00]' : ''}`}
                onClick={(e) => disabled && e.preventDefault()}
                data-testid={`nav-${path.split('/').pop() || 'dashboard'}`}
              >
                <span className={isActive ? 'text-[#FFB000]' : ''}>{icon}</span>
                {sidebarOpen && (
                  <span className="ml-3 font-mono text-sm">
                    {label}
                    {phase && <span className="ml-2 text-xs text-gray-500">(Phase {phase})</span>}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* User */}
        <div className="border-t border-[#1a1a1a] p-4">
          {sidebarOpen ? (
            <div className="flex items-center justify-between">
              <div className="truncate">
                <p className="text-sm font-medium truncate">{user?.email}</p>
                <p className="text-xs text-[#FFB000] font-mono">{user?.primary_role}</p>
              </div>
              <button
                onClick={logout}
                className="text-gray-400 hover:text-[#E1000F] transition-colors"
                data-testid="logout-btn"
              >
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              </button>
            </div>
          ) : (
            <button
              onClick={logout}
              className="w-full flex justify-center text-gray-400 hover:text-[#E1000F]"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
              </svg>
            </button>
          )}
        </div>

        {/* Collapse toggle */}
        <button
          onClick={() => setSidebarOpen(!sidebarOpen)}
          className="absolute bottom-20 -right-3 w-6 h-6 bg-[#1a1a1a] border border-[#333] rounded-full flex items-center justify-center text-gray-400 hover:text-white"
        >
          <svg 
            className={`w-4 h-4 transition-transform ${sidebarOpen ? '' : 'rotate-180'}`} 
            fill="none" viewBox="0 0 24 24" stroke="currentColor"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
        </button>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col" data-testid="main-content">
        {/* Top Bar */}
        <header className="h-16 bg-[#0a0a0a] border-b border-[#1a1a1a] flex items-center justify-between px-6">
          <div className="flex items-center space-x-4">
            <h1 className="text-lg font-mono text-[#FFB000]">
              {navItems.find(item => item.path === location.pathname)?.label || 'Dashboard'}
            </h1>
          </div>
          
          {/* Status indicators */}
          <div className="flex items-center space-x-4 font-mono text-xs">
            <div className="flex items-center space-x-2">
              <span className="w-2 h-2 bg-[#33FF00] rounded-full animate-pulse"></span>
              <span className="text-gray-400">API</span>
            </div>
            <div className="flex items-center space-x-2">
              <span className="w-2 h-2 bg-[#FFB000] rounded-full"></span>
              <span className="text-gray-400">DB: not configured</span>
            </div>
            <Link 
              to="/admin/config" 
              className="text-gray-400 hover:text-[#FFB000]"
              title="Credentials"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
              </svg>
            </Link>
            <Link 
              to="/" 
              className="text-[#00FFFF] hover:underline"
            >
              Site public
            </Link>
          </div>
        </header>

        {/* Page Content */}
        <main className="flex-1 p-6 overflow-auto">
          <Outlet />
        </main>

        {/* Footer with developer credits */}
        <footer className="bg-[#0a0a0a] border-t border-[#1a1a1a] py-3 px-6">
          <div className="flex items-center justify-between text-xs text-gray-500 font-mono">
            <div className="flex items-center space-x-4">
              <span>IFRAP-AI v1.3.0</span>
              <span className="text-gray-700">|</span>
              <span>
                <span className="text-gray-400">Pool Presse Logiciel</span>, 
                <span className="text-gray-400"> Academie-IA</span> & 
                <span className="text-gray-400"> J-B Giraud</span>
                <span className="text-gray-600"> • Dir. A. Verdier-Molinié</span>
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <Link to="/admin/documentation" className="text-gray-400 hover:text-[#FFB000]">Documentation</Link>
              <button 
                onClick={() => {
                  localStorage.removeItem('ifrap_tutorial_completed');
                  sessionStorage.removeItem('ifrap_boot_shown');
                  window.location.reload();
                }}
                className="text-gray-400 hover:text-[#FFB000]"
              >
                Tutoriel
              </button>
            </div>
          </div>
        </footer>
      </div>

      {/* CyberAgnès Chat Widget - Admin mode */}
      <CyberAgnesChat variant="admin" />
    </div>
  );
};

export default AdminLayout;
